import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
export function ThreeHero() {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const scene = new THREE.Scene();
    // Camera setup
    const camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 1000);
    camera.position.set(0, 1.5, 4);
    camera.lookAt(0, 0, 0);
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    // Flowing Golden Mesh Terrain
    const planeGeometry1 = new THREE.PlaneGeometry(20, 12, 80, 50);
    const planeGeometry2 = new THREE.PlaneGeometry(20, 12, 60, 40);
    // Store original vertices for animation
    const pos1 = planeGeometry1.attributes.position;
    const pos2 = planeGeometry2.attributes.position;
    const origZ1 = new Float32Array(pos1.count);
    const origZ2 = new Float32Array(pos2.count);
    for (let i = 0; i < pos1.count; i++) {
      origZ1[i] = pos1.getZ(i);
    }
    for (let i = 0; i < pos2.count; i++) {
      origZ2[i] = pos2.getZ(i);
    }
    const wireframeMaterial = new THREE.MeshBasicMaterial({
      color: 0xc9953c,
      wireframe: true,
      transparent: true,
      opacity: 0.15
    });
    const plane1 = new THREE.Mesh(planeGeometry1, wireframeMaterial);
    plane1.rotation.x = -Math.PI / 2;
    plane1.position.y = -1;
    scene.add(plane1);
    const plane2 = new THREE.Mesh(planeGeometry2, wireframeMaterial);
    plane2.rotation.x = -Math.PI / 2;
    plane2.rotation.z = 0.1;
    plane2.position.y = -1.2;
    scene.add(plane2);
    // Floating Orbs
    const orbCount = 20;
    const orbs: {
      mesh: THREE.Mesh;
      speed: number;
      offset: number;
    }[] = [];
    const orbGeometry = new THREE.SphereGeometry(1, 16, 16);
    const orbMaterial = new THREE.MeshBasicMaterial({
      color: 0xc9953c,
      transparent: true,
      opacity: 0.6
    });
    for (let i = 0; i < orbCount; i++) {
      const mesh = new THREE.Mesh(orbGeometry, orbMaterial);
      const scale = 0.03 + Math.random() * 0.03;
      mesh.scale.set(scale, scale, scale);
      mesh.position.x = (Math.random() - 0.5) * 16;
      mesh.position.y = -2 + Math.random() * 6;
      mesh.position.z = (Math.random() - 0.5) * 8 - 2;
      scene.add(mesh);
      orbs.push({
        mesh,
        speed: 0.005 + Math.random() * 0.01,
        offset: Math.random() * Math.PI * 2
      });
    }
    // Subtle Particle Field
    const particleCount = 150;
    const particleGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particleCount * 3);
    const particleVelocities = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      particlePositions[i * 3] = (Math.random() - 0.5) * 20;
      particlePositions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      particlePositions[i * 3 + 2] = (Math.random() - 0.5) * 10 - 2;
      particleVelocities[i * 3] = (Math.random() - 0.5) * 0.002;
      particleVelocities[i * 3 + 1] = Math.random() * 0.002 + 0.001; // Drift upward
      particleVelocities[i * 3 + 2] = (Math.random() - 0.5) * 0.002;
    }
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    const particleMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.02,
      transparent: true,
      opacity: 0.3,
      sizeAttenuation: true
    });
    const particles = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particles);
    // Animation Loop
    let animationId: number;
    let time = 0;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      time += 0.01;
      // Animate Terrain 1
      const positions1 = planeGeometry1.attributes.position;
      for (let i = 0; i < positions1.count; i++) {
        const x = positions1.getX(i);
        const y = positions1.getY(i);
        const z = Math.sin(x * 0.5 + time) * 0.3 + Math.cos(y * 0.5 + time * 0.8) * 0.3;
        positions1.setZ(i, origZ1[i] + z);
      }
      positions1.needsUpdate = true;
      // Animate Terrain 2
      const positions2 = planeGeometry2.attributes.position;
      for (let i = 0; i < positions2.count; i++) {
        const x = positions2.getX(i);
        const y = positions2.getY(i);
        const z = Math.sin(x * 0.4 - time * 0.9) * 0.4 + Math.cos(y * 0.6 - time * 0.7) * 0.4;
        positions2.setZ(i, origZ2[i] + z);
      }
      positions2.needsUpdate = true;
      // Animate Orbs
      orbs.forEach(orb => {
        orb.mesh.position.y += orb.speed;
        orb.mesh.position.x += Math.sin(time * 0.5 + orb.offset) * 0.005;
        if (orb.mesh.position.y > 4) {
          orb.mesh.position.y = -2;
          orb.mesh.position.x = (Math.random() - 0.5) * 16;
        }
      });
      // Animate Particles
      const pPositions = particleGeometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        pPositions[i * 3] += particleVelocities[i * 3];
        pPositions[i * 3 + 1] += particleVelocities[i * 3 + 1];
        pPositions[i * 3 + 2] += particleVelocities[i * 3 + 2];
        // Reset if too high
        if (pPositions[i * 3 + 1] > 5) {
          pPositions[i * 3 + 1] = -5;
          pPositions[i * 3] = (Math.random() - 0.5) * 20;
        }
      }
      particleGeometry.attributes.position.needsUpdate = true;
      renderer.render(scene, camera);
    };
    animate();
    // Resize Handler
    const handleResize = () => {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);
    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      planeGeometry1.dispose();
      planeGeometry2.dispose();
      wireframeMaterial.dispose();
      orbGeometry.dispose();
      orbMaterial.dispose();
      particleGeometry.dispose();
      particleMaterial.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);
  return <div ref={containerRef} className="absolute inset-0 z-0" aria-hidden="true" data-id="element-41" />;
}